/**
 * PKMProjectsTab — project workspaces with deep-dive summaries.
 * Renders inside the PKM Notes page as a tab.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Sparkles, Loader2, FolderKanban, Trash2, Edit, FileText, Target, AlertTriangle, CheckCircle, Link2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PKMProjectsTab = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [deepDiveLoading, setDeepDiveLoading] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', color: '#002FA7' });

  // Link-notes dialog state (Stage 6 retrofit UX for notes created before the Project entity)
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkable, setLinkable] = useState([]);
  const [linkableLoading, setLinkableLoading] = useState(false);
  const [selectedLinkIds, setSelectedLinkIds] = useState(new Set());
  const [linkSubmitting, setLinkSubmitting] = useState(false);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pkm/projects`, { headers });
      setProjects(res.data);
    } catch {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchDetail = async (project_id) => {
    try {
      const res = await axios.get(`${API}/pkm/projects/${project_id}`, { headers });
      setDetail(res.data);
    } catch {
      toast.error('Failed to load project');
    }
  };

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { if (selected) fetchDetail(selected); /* eslint-disable-next-line */ }, [selected]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/pkm/projects/${editing.project_id}`, form, { headers });
        toast.success('Project updated');
      } else {
        await axios.post(`${API}/pkm/projects`, form, { headers });
        toast.success('Project created');
      }
      setIsOpen(false); setEditing(null); setForm({ name: '', description: '', color: '#002FA7' });
      fetchProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Notes will keep their project tag.`)) return;
    try {
      await axios.delete(`${API}/pkm/projects/${p.project_id}`, { headers });
      toast.success('Deleted');
      if (selected === p.project_id) { setSelected(null); setDetail(null); }
      fetchProjects();
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleDeepDive = async () => {
    if (!selected) return;
    setDeepDiveLoading(true);
    try {
      const res = await axios.post(`${API}/pkm/projects/${selected}/deep-dive`, {}, { headers });
      toast.success('Deep dive generated');
      setDetail(prev => ({ ...prev, last_deep_dive_at: res.data.generated_at, last_deep_dive_summary: res.data.summary }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Deep dive failed');
    } finally {
      setDeepDiveLoading(false);
    }
  };

  const openLinkDialog = async () => {
    if (!selected) return;
    setLinkOpen(true);
    setLinkableLoading(true);
    setSelectedLinkIds(new Set());
    try {
      const res = await axios.get(`${API}/pkm/projects/${selected}/linkable-notes`, { headers });
      setLinkable(res.data.notes || []);
    } catch {
      toast.error('Failed to load notes');
    } finally {
      setLinkableLoading(false);
    }
  };

  const toggleLinkId = (note_id) => {
    setSelectedLinkIds(prev => {
      const next = new Set(prev);
      if (next.has(note_id)) next.delete(note_id); else next.add(note_id);
      return next;
    });
  };

  const handleLinkNotes = async () => {
    if (!selected || selectedLinkIds.size === 0) return;
    setLinkSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/pkm/projects/${selected}/link-notes`,
        { note_ids: Array.from(selectedLinkIds) },
        { headers },
      );
      toast.success(`${res.data.updated} note${res.data.updated === 1 ? '' : 's'} linked to "${res.data.project_name}"`);
      setLinkOpen(false);
      setSelectedLinkIds(new Set());
      // Refresh the project detail + project list counts
      fetchProjects();
      fetchDetail(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Link failed');
    } finally {
      setLinkSubmitting(false);
    }
  };

  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, description: p.description || '', color: p.color || '#002FA7' }); setIsOpen(true); };
  const openNew = () => { setEditing(null); setForm({ name: '', description: '', color: '#002FA7' }); setIsOpen(true); };

  const dd = detail?.last_deep_dive_summary;

  return (
    <div className="space-y-4" data-testid="pkm-projects-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-1.5"><FolderKanban className="w-4 h-4 text-[#002FA7]" />Project workspaces</h3>
        <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={openNew} data-testid="new-project-btn">
          <Plus className="w-4 h-4 mr-1" />New project
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT: list */}
        <div className="lg:col-span-1 space-y-2 max-h-[600px] overflow-y-auto">
          {loading ? <p className="text-xs text-[#9CA3AF]">Loading...</p>
            : projects.length === 0 ? (
              <Card className="border-[#E5E7EB]"><CardContent className="p-8 text-center text-[#9CA3AF] text-sm">No projects yet — create one to group related notes and get AI deep-dive summaries.</CardContent></Card>
            ) : projects.map(p => (
              <Card
                key={p.project_id}
                className={`border-[#E5E7EB] cursor-pointer hover:shadow-sm transition-all ${selected === p.project_id ? 'border-[#002FA7] bg-[#002FA7]/5' : ''}`}
                onClick={() => setSelected(p.project_id)}
                data-testid={`project-card-${p.project_id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-start gap-2">
                    <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: (p.color || '#002FA7') + '15' }}>
                      <FolderKanban className="w-3.5 h-3.5" style={{ color: p.color || '#002FA7' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-semibold truncate">{p.name}</h4>
                      <p className="text-[10px] text-[#9CA3AF] truncate mt-0.5">{p.description || '—'}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Badge variant="secondary" className="text-[8px]">{p.note_count} notes</Badge>
                        {p.last_deep_dive_at && <Badge className="text-[8px] bg-[#FF9500]/10 text-[#FF9500]"><Sparkles className="w-2 h-2 mr-0.5" />Deep dive</Badge>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>

        {/* RIGHT: detail */}
        <div className="lg:col-span-2">
          {!detail ? (
            <Card className="border-[#E5E7EB] h-full"><CardContent className="p-12 text-center text-[#9CA3AF]"><FolderKanban className="w-10 h-10 mx-auto mb-3 text-[#E5E7EB]" /><p className="text-sm">Select a project to see its notes and deep-dive summary</p></CardContent></Card>
          ) : (
            <Card className="border-[#E5E7EB]" data-testid="project-detail">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{detail.name}</CardTitle>
                    <p className="text-xs text-[#9CA3AF] mt-0.5">{detail.description || '—'}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openEdit(detail)} data-testid="edit-project-btn"><Edit className="w-3 h-3 mr-1" />Edit</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5" onClick={openLinkDialog} data-testid="link-notes-btn"><Link2 className="w-3 h-3 mr-1" />Link notes</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-[#FF9500] text-[#FF9500] hover:bg-[#FF9500]/5" onClick={handleDeepDive} disabled={deepDiveLoading} data-testid="deep-dive-btn">
                      {deepDiveLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                      Generate Deep Dive
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-[#FF3B30]" onClick={() => handleDelete(detail)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Deep Dive summary */}
                {dd ? (
                  <div className="border border-[#FF9500]/30 bg-[#FF9500]/5 rounded-lg p-3 space-y-3" data-testid="deep-dive-panel">
                    <div className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#FF9500]" /><h4 className="text-xs font-semibold text-[#FF9500]">AI Deep Dive</h4>
                      <span className="text-[10px] text-[#9CA3AF] ml-auto">{detail.last_deep_dive_at ? new Date(detail.last_deep_dive_at).toLocaleString() : ''}</span>
                    </div>
                    <p className="text-xs text-[#4B5563] leading-relaxed">{dd.summary}</p>
                    {(dd.key_themes || []).length > 0 && (
                      <div><p className="text-[10px] font-semibold text-[#4B5563] mb-1">Key themes</p>
                        <div className="flex flex-wrap gap-1">{dd.key_themes.map((t, i) => <Badge key={i} variant="secondary" className="text-[10px]">{t}</Badge>)}</div>
                      </div>
                    )}
                    {(dd.open_questions || []).length > 0 && (
                      <div><p className="text-[10px] font-semibold text-[#4B5563] mb-1 flex items-center gap-1"><Target className="w-3 h-3 text-[#007AFF]" />Open questions</p>
                        <ul className="text-[11px] text-[#4B5563] space-y-0.5 list-disc pl-4">{dd.open_questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
                      </div>
                    )}
                    {(dd.risks || []).length > 0 && (
                      <div><p className="text-[10px] font-semibold text-[#FF3B30] mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Risks</p>
                        <ul className="text-[11px] text-[#4B5563] space-y-0.5 list-disc pl-4">{dd.risks.map((r, i) => <li key={i}>{r}</li>)}</ul>
                      </div>
                    )}
                    {(dd.recommended_next_steps || []).length > 0 && (
                      <div><p className="text-[10px] font-semibold text-[#34C759] mb-1 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Recommended next steps</p>
                        <ul className="text-[11px] text-[#4B5563] space-y-0.5 list-disc pl-4">{dd.recommended_next_steps.map((s, i) => <li key={i}>{s}</li>)}</ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border border-dashed border-[#E5E7EB] rounded-lg p-6 text-center">
                    <Sparkles className="w-8 h-8 text-[#E5E7EB] mx-auto mb-2" />
                    <p className="text-xs text-[#9CA3AF]">No deep dive yet. Click <em>Generate Deep Dive</em> to get an AI summary across all notes in this project.</p>
                  </div>
                )}

                {/* Notes list */}
                <div>
                  <h4 className="text-xs font-semibold mb-2 flex items-center gap-1"><FileText className="w-3.5 h-3.5 text-[#002FA7]" />Notes ({detail.notes?.length || 0})</h4>
                  {(detail.notes || []).length === 0
                    ? (
                      <div className="border border-dashed border-[#FDBA74] bg-[#FFF7ED] rounded-lg p-4 text-center">
                        <p className="text-xs text-[#9A3412] mb-2">No notes in this project yet.</p>
                        <p className="text-[10px] text-[#C2410C] mb-3">
                          Have existing notes that belong here? Click <strong>Link notes</strong> above to bulk-assign them —
                          then run the Deep Dive.
                        </p>
                        <Button size="sm" className="bg-[#002FA7] hover:bg-[#00227A] h-7 text-xs" onClick={openLinkDialog} data-testid="link-notes-empty-btn">
                          <Link2 className="w-3 h-3 mr-1" />Link existing notes
                        </Button>
                      </div>
                    )
                    : (
                      <div className="space-y-1 max-h-[220px] overflow-y-auto">
                        {detail.notes.map(n => (
                          <div key={n.note_id} className="flex items-center gap-2 p-2 rounded border border-[#E5E7EB] text-xs">
                            <FileText className="w-3 h-3 text-[#9CA3AF]" />
                            <span className="text-[#4B5563] truncate">{n.title}</span>
                            <Badge variant="secondary" className="text-[8px] ml-auto">{n.note_type}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent data-testid="project-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Project' : 'New Project'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Q3 Billing Rewrite" data-testid="project-name-input" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What this project is about..." /></div>
            <div><Label>Color</Label><Input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="h-10" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleSave} data-testid="save-project-btn">{editing ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Link-notes dialog — retrofit orphan notes into a project */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-2xl" data-testid="link-notes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Link2 className="w-4 h-4 text-[#002FA7]" />
              Link notes to {detail?.name ? `"${detail.name}"` : 'this project'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            <p className="text-xs text-[#6B7280]">
              Select the notes that belong to this project. Each note's <code>project</code> field will be
              set to <strong>{detail?.name}</strong> so the Deep Dive + streak counts pick them up.
            </p>
            {linkableLoading ? (
              <p className="text-xs text-[#9CA3AF] p-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Loading…</p>
            ) : linkable.length === 0 ? (
              <p className="text-xs text-[#9CA3AF] p-4 text-center">No eligible notes found — everything's either already linked or archived.</p>
            ) : (
              linkable.map(n => (
                <label key={n.note_id} className="flex items-start gap-2 p-2 rounded border border-[#E5E7EB] hover:border-[#002FA7]/40 cursor-pointer text-xs">
                  <input
                    type="checkbox"
                    checked={selectedLinkIds.has(n.note_id)}
                    onChange={() => toggleLinkId(n.note_id)}
                    className="mt-0.5"
                    data-testid={`link-note-${n.note_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#0A0A0A] truncate">{n.title || '(untitled)'}</p>
                    <div className="flex items-center gap-2 mt-0.5 text-[10px] text-[#9CA3AF]">
                      <Badge variant="secondary" className="text-[9px]">{n.note_type || 'note'}</Badge>
                      {n.project ? <span>current: <code className="text-[#FF9500]">{n.project}</code></span> : <span className="italic">no project</span>}
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>
          <DialogFooter>
            <div className="text-[11px] text-[#6B7280] mr-auto self-center">
              {selectedLinkIds.size} selected
            </div>
            <Button variant="outline" onClick={() => setLinkOpen(false)}>Cancel</Button>
            <Button
              className="bg-[#002FA7] hover:bg-[#00227A]"
              onClick={handleLinkNotes}
              disabled={selectedLinkIds.size === 0 || linkSubmitting}
              data-testid="confirm-link-notes-btn"
            >
              {linkSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Link {selectedLinkIds.size > 0 ? selectedLinkIds.size : ''} note{selectedLinkIds.size === 1 ? '' : 's'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PKMProjectsTab;
